import React from 'react';
import SearchAndPagination from './SearchAndPagination';

/**
 * AssignedUsersTab
 *
 * Props:
 * - assignedUsers: array
 * - handleResumeSession: (sessionId) => void
 * - handleStartSessionClick: (user) => void
 */
const AssignedUsersTab = ({
  assignedUsers,
  handleResumeSession,
  handleStartSessionClick,
  openingModal = false,
  openingModalUserId = null
}) => {
  const renderUsers = (usersToRender) => {
    if (!usersToRender.length) {
      return (
        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
          <svg
            className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
          <p>No assigned users found</p>
        </div>
      );
    }

    return usersToRender.map((user) => (
    <div key={user.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {user.name}
            </h3>
            {(user.has_pending_request || user.has_pending_session) && user.pending_session && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 animate-pulse">
                Requested by User
              </span>
            )}
            {user.has_active_session && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">
                Active Session
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
            <div>
              <span className="font-medium">Email:</span> {user.email}
            </div>
            {user.company && (
              <div>
                <span className="font-medium">Company:</span> {user.company}
              </div>
            )}
            <div>
              <span className="font-medium">Assigned:</span>{' '}
              {user.created_at && !isNaN(new Date(user.created_at).getTime())
                ? new Date(user.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })
                : user.assigned_at && !isNaN(new Date(user.assigned_at).getTime())
                ? new Date(user.assigned_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })
                : 'N/A'}
            </div>
          </div>
        </div>
        <div className="ml-6 flex flex-col space-y-2">
          {user.has_active_session && user.active_session ? (
            <button
              onClick={() => handleResumeSession(user.active_session.id)}
              className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Resume Session
            </button>
          ) : (
            <button
              onClick={() => handleStartSessionClick(user)}
              disabled={openingModal && openingModalUserId === user.id}
              className={`${
                (user.has_pending_request || user.has_pending_session) && user.pending_session
                  ? 'bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-600'
                  : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600'
              } text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center min-w-[180px] disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {openingModal && openingModalUserId === user.id ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Opening...
                </>
              ) : (user.has_pending_request || user.has_pending_session) && user.pending_session ? (
                <>
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  Pending Session requested by user
                </>
              ) : (
                'Start Session'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
    ));
  };

  if (!assignedUsers.length) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        <svg
          className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
        <p>No assigned users</p>
      </div>
    );
  }

  return (
    <SearchAndPagination
      data={assignedUsers}
      searchFields={['name', 'email', 'company']}
      itemsPerPage={10}
      searchPlaceholder="Search users by name, email, or company..."
      renderItems={renderUsers}
    />
  );
};

export default AssignedUsersTab;


